import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AppShell, { Protected } from "@/components/AppShell";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { FilePdf, ArrowLeft } from "@phosphor-icons/react";
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Tooltip } from "recharts";

function displayAnswer(q, val) {
  if (val == null || val === "") return "—";
  if (Array.isArray(val)) return val.join(", ");
  if (q.type === "yes_no") return val === true || val === "yes" ? "Oui" : "Non";
  if (["single_choice", "multiple_choice", "likert"].includes(q.type)) {
    const opt = (q.options || []).find((o) => o.label === val || o.value === val);
    return opt ? opt.label : String(val);
  }
  return String(val);
}

export default function ResponseDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/responses/${id}`);
        setData(data);
      } catch (e) {
        setError(e?.response?.status === 404
          ? "Cette réponse est introuvable — elle a peut-être été supprimée ou anonymisée."
          : (e?.response?.data?.detail || "Impossible de charger cette réponse."));
      }
    })();
  }, [id]);

  const downloadPdf = async () => {
    const res = await fetch(`${API}/responses/${id}/pdf`, {
      credentials: "include",
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `reponse-${id}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };

  if (error) {
    return (
      <Protected>
        <AppShell
          title="Réponse introuvable"
          actions={
            <Button variant="outline" className="rounded-md" onClick={() => nav(-1)}>
              <ArrowLeft size={16} className="mr-1.5" /> Retour
            </Button>
          }
        >
          <div className="bg-white border border-[#E1E9F0] rounded-2xl p-16 text-center max-w-2xl mx-auto">
            <p className="font-serif text-2xl text-[#1E3A5F] mb-3">Réponse introuvable</p>
            <p className="text-[#5F758F]" data-testid="response-error-message">{error}</p>
          </div>
        </AppShell>
      </Protected>
    );
  }

  if (!data) return null;
  const { response, template } = data;

  const subTone = (interp) => {
    const s = (interp || "").toLowerCase();
    if (/sévère|severe|important|critique/.test(s)) return "crit";
    if (/modér|moder|léger|leger/.test(s)) return "warn";
    return "normal";
  };
  const globalTone = subTone(response.score?.interpretation);
  const initials = `${(response.patient_name || "?").split(" ").map((w) => w.charAt(0)).slice(0, 2).join("")}`.toUpperCase();

  return (
    <Protected>
      <AppShell
        title="Détail de la réponse"
        actions={
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-[10px] font-semibold" onClick={() => nav(-1)}>
              <ArrowLeft size={16} className="mr-1.5" /> Retour
            </Button>
            <Button className="rounded-[10px] bg-[#3A8FA5] hover:bg-[#164E5F] text-white font-semibold" onClick={downloadPdf} data-testid="pdf-download-btn">
              <FilePdf size={16} className="mr-1.5" /> Télécharger PDF
            </Button>
          </div>
        }
      >
        {/* Head-card — patient + questionnaire + score global as pills */}
        <div className="bg-white border border-[#E1E9F0] rounded-2xl p-8 mb-8 grid grid-cols-[auto_1fr_auto] gap-6 items-center shadow-[0_1px_3px_rgba(30,58,95,0.04)]">
          <div className="w-[68px] h-[68px] rounded-[14px] bg-[#3A8FA5] text-white flex items-center justify-center font-bold text-2xl">
            {initials || "??"}
          </div>
          <div className="min-w-0">
            <h2 className="text-[26px] font-bold tracking-tight text-[#1E3A5F] leading-tight">{response.patient_name}</h2>
            <p className="text-[14px] text-[#5F758F] mt-1 flex items-center gap-2 flex-wrap">
              <span className="text-[#1E3A5F] font-medium">{response.template_title}</span>
              {!template && (
                <span className="inline-flex items-center rounded-[4px] bg-[#FFE0DC] text-[#8B2A1E] border border-[#EEC1BB] px-2 py-0.5 text-[10px] tracking-[0.15em] uppercase font-semibold italic" data-testid="tpl-status-missing">
                  Questionnaire supprimé
                </span>
              )}
              {template?.is_deleted && (
                <span className="inline-flex items-center rounded-[4px] bg-[#FFE0DC] text-[#8B2A1E] border border-[#EEC1BB] px-2 py-0.5 text-[10px] tracking-[0.15em] uppercase font-semibold italic" data-testid="tpl-status-deleted">
                  Supprimé{template.deleted_at ? ` le ${new Date(template.deleted_at).toLocaleDateString("fr-FR")}` : ""}
                </span>
              )}
              <span>·</span>
              <span>{new Date(response.submitted_at).toLocaleString("fr-FR", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
              {response.patient_email && <><span>·</span><span>{response.patient_email}</span></>}
            </p>
          </div>
          {response.score && (
            <div className="text-right shrink-0 min-w-[160px]">
              <p className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold mb-1">Score global</p>
              <p className="text-[36px] font-bold text-[#1E3A5F] leading-none tracking-tight">
                {response.score.percentage != null ? `${response.score.percentage} %` : `${response.score.total} / ${response.score.max}`}
              </p>
              {response.score.interpretation && (
                <p
                  className={`mt-2.5 inline-block text-[12px] font-semibold px-3 py-1.5 rounded-full whitespace-nowrap border ${
                    globalTone === "crit"
                      ? "bg-[#FFE0DC] text-[#8B2A1E] border-[#EEC1BB]"
                      : globalTone === "warn"
                        ? "bg-[#FFF3D6] text-[#B76A00] border-[#F0DDA8]"
                        : "bg-[#E6F1F5] text-[#3A8FA5] border-[#C7DEE6]"
                  }`}
                  data-testid="score-interp"
                >
                  {response.score.interpretation}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Sub-scores — tricolor pills, above the radar */}
        {Array.isArray(response.score?.subscores) && response.score.subscores.length > 0 && (
          <div className="mb-6" data-testid="score-subscores">
            <p className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold mb-3">Sous-scores par dimension</p>
            <div className="flex flex-wrap gap-2">
              {response.score.subscores.map((s) => {
                const tone = subTone(s.interpretation);
                const cls = tone === "crit"
                  ? "bg-[#FFE0DC] border-[#EEC1BB] text-[#8B2A1E]"
                  : tone === "warn"
                    ? "bg-[#FFF3D6] border-[#F0DDA8] text-[#1E3A5F]"
                    : "bg-[#EEF3F8] border-[#E1E9F0] text-[#1E3A5F]";
                const iCls = tone === "crit" ? "text-[#8B2A1E] font-semibold" : "text-[#D45A3F]";
                return (
                  <span key={s.key} className={`inline-flex items-baseline gap-2 px-4 py-2 rounded-full text-[13px] font-medium border ${cls}`}>
                    <span className={tone === "normal" ? "text-[#5F758F]" : ""}>{s.label}</span>
                    <span className="font-bold tabular-nums">{s.total}/{s.max}</span>
                    {s.interpretation && <span className={`text-[12px] ${iCls}`}>· {s.interpretation}</span>}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {/* Radar chart — visual snapshot of sub-scores (only when >= 3 dimensions, otherwise a radar is meaningless). */}
        {Array.isArray(response.score?.subscores) && response.score.subscores.length >= 3 && (
          <div className="bg-white border border-[#E1E9F0] rounded-2xl p-6 mb-8" data-testid="score-radar">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Profil par dimension</p>
                <p className="text-[20px] font-bold text-[#1E3A5F] mt-1 tracking-tight">Radar des sous-scores</p>
              </div>
              <p className="text-[12px] text-[#5F758F] italic">valeur = % du max de la dimension</p>
            </div>
            <ResponsiveContainer width="100%" height={320}>
              <RadarChart data={response.score.subscores.map((s) => ({
                dim: s.label,
                pct: s.max > 0 ? Math.round((s.total / s.max) * 100) : 0,
                raw: `${s.total}/${s.max}`,
              }))}>
                <PolarGrid stroke="#E1E9F0" />
                <PolarAngleAxis dataKey="dim" tick={{ fontSize: 12, fill: "#1E3A5F", fontWeight: 600 }} />
                <PolarRadiusAxis domain={[0, 100]} tick={{ fontSize: 10, fill: "#5F758F" }} tickCount={5} />
                <Radar name="Score" dataKey="pct" stroke="#3A8FA5" fill="#3A8FA5" fillOpacity={0.45} />
                <Tooltip
                  contentStyle={{ borderRadius: 10, border: "1px solid #E1E9F0", fontSize: 12, fontFamily: "Manrope, sans-serif" }}
                  formatter={(v, _n, ctx) => [`${v}% (${ctx.payload.raw})`, ctx.payload.dim]}
                  labelFormatter={() => ""}
                />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Answers — one card per question */}
        <div className="flex items-center gap-3 mb-4">
          <h3 className="text-xl font-bold text-[#1E3A5F]">Réponses détaillées</h3>
          <span className="bg-[#E6F1F5] text-[#3A8FA5] text-[13px] px-2.5 py-0.5 rounded-full font-bold">
            {template?.questions?.length || Object.keys(response.answers || {}).length}
          </span>
        </div>
        <div className="flex flex-col gap-2.5">
          {template?.questions ? (
            template.questions.map((q, i) => (
              <div key={q.id} className="bg-white border border-[#E1E9F0] rounded-[14px] px-6 py-5 grid grid-cols-[auto_1fr] gap-5 items-start" data-testid={`answer-${q.id}`}>
                <span className="w-8 h-8 rounded-[10px] bg-[#EEF3F8] border border-[#E1E9F0] text-[#3A8FA5] flex items-center justify-center font-bold text-[13px] shrink-0 tabular-nums">
                  {i + 1}
                </span>
                <div className="min-w-0">
                  <p className="text-[16px] font-semibold text-[#1E3A5F] mb-2 leading-snug">{q.label}</p>
                  <p className="text-[15px] text-[#3A8FA5] font-medium">{displayAnswer(q, response.answers[q.id])}</p>
                </div>
              </div>
            ))
          ) : (
            Object.entries(response.answers || {}).map(([qid, val], i) => (
              <div key={qid} className="bg-white border border-[#E1E9F0] rounded-[14px] px-6 py-5 grid grid-cols-[auto_1fr] gap-5 items-start" data-testid={`answer-raw-${qid}`}>
                <span className="w-8 h-8 rounded-[10px] bg-[#FFE0DC] border border-[#EEC1BB] text-[#8B2A1E] flex items-center justify-center font-bold text-[13px] shrink-0 tabular-nums">
                  {i + 1}
                </span>
                <div className="min-w-0">
                  <p className="text-[13px] text-[#5F758F] italic mb-1">Libellé indisponible — questionnaire supprimé (id : {qid})</p>
                  <p className="text-[15px] text-[#3A8FA5] font-medium">{Array.isArray(val) ? val.join(", ") : val == null || val === "" ? "—" : String(val)}</p>
                </div>
              </div>
            ))
          )}
        </div>
      </AppShell>
    </Protected>
  );
}
